// src/components/cards/LeadCard.jsx
import React, { useState, useEffect } from 'react';
import { 
    FiHash, FiFileText, FiUser, FiLayers, 
    FiActivity, FiChevronDown, FiList, FiEye, FiClock, 
    FiBriefcase, FiPhone, FiMail, FiTarget
} from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';

import SupremeLeadModal from '../modals/SupremeLeadModal/index';
import { LeadHistoryModal, LeadLogModal, StatusUpdateLeadModal } from '../modals/LeadIntelModals';
import ManualOrderModal from '../modals/ManualOrderModal';

export const LEAD_STATUS_MAP = [
    { id: 'NOVO', label: 'Aguardando Triagem', color: 'bg-emerald-500' },
    { id: 'TRIAGEM', label: 'Análise Inicial', color: 'bg-blue-600' },
    { id: 'QUALIFICACAO', label: 'Validação de Dados', color: 'bg-indigo-500' },
    { id: 'EM_ATENDIMENTO', label: 'Contato Iniciado', color: 'bg-cyan-500' },
    { id: 'APRESENTACAO', label: 'Apresentação / Reunião', color: 'bg-yellow-600' },
    { id: 'PROPOSTA_ENVIADA', label: 'Proposta Enviada', color: 'bg-purple-500' },
    { id: 'NEGOCIACAO', label: 'Em Negociação', color: 'bg-orange-500' },
    { id: 'FECHADO_GANHO', label: 'Fechado (Ganho)', color: 'bg-emerald-600' },
    { id: 'FECHADO_PERDIDO', label: 'Fechado (Perdido)', color: 'bg-red-800' },
    { id: 'CONGELADO', label: 'Congelado / Pausado', color: 'bg-gray-600' }
];

export default function LeadCard({ lead, onStatusUpdate, readOnly = false }) {
    const [modalOpen, setModalOpen] = useState(null);
    const [pendingStatus, setPendingStatus] = useState(null);
    const [showOrderModal, setShowOrderModal] = useState(false);

    const nome = lead.name || lead.nome || 'Desconhecido';
    const empresa = lead.razaoSocial || lead.empresa?.razaoSocial || 'Sem Empresa';
    const cnpj = lead.cnpj || lead.empresa?.cnpj || 'S/N';
    const origem = lead.origem || lead.source || 'INTERNET';
    
    const currentStatusData = LEAD_STATUS_MAP.find(s => s.id === lead.statusComercial) || { label: lead.statusComercial, color: 'bg-gray-400' };

    // ⏱️ TIMER E ALERTA SUPREMO DE ROTTING (APODRECIMENTO)
    const [currentTime, setCurrentTime] = useState(Date.now());
    
    useEffect(() => {
        if (lead.statusComercial === 'NOVO' || lead.statusComercial === 'TRIAGEM') {
            const interval = setInterval(() => setCurrentTime(Date.now()), 1000);
            return () => clearInterval(interval);
        }
    }, [lead.statusComercial]);

    const createdAtDate = lead.createdAt ? new Date(lead.createdAt).getTime() : Date.now();
    const diffMs = currentTime - createdAtDate;
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffDays = Math.floor(diffMins / (60 * 24));

    let timerColor = 'text-emerald-500 border-emerald-500/30';
    let timerStatus = `${diffDays} Dias Ativo`;
    let isFrozen = false;
    let isRottingAlert = false;

    if (lead.statusComercial === 'NOVO') {
        if (diffMins >= 5) {
            timerColor = 'text-red-600 border-red-600 bg-red-600/10 shadow-[0_0_30px_rgba(220,38,38,0.5)] animate-pulse';
            timerStatus = `${diffMins} MINS (PERDENDO LEAD!)`;
            isRottingAlert = true;
        } else if (diffSecs >= 30) {
            timerColor = 'text-orange-500 border-orange-500 bg-orange-500/10 animate-pulse';
            timerStatus = `${diffSecs} SEGS (ESFRIANDO)`;
        } else {
            timerColor = 'text-emerald-500 border-emerald-500 bg-emerald-500/5';
            timerStatus = `${diffSecs} SEGS (QUENTE)`;
        }
    } else {
        if (diffDays >= 40 || lead.statusComercial === 'CONGELADO') {
            timerColor = 'text-gray-400 border-gray-500/30';
            timerStatus = 'CONGELADO / AGUARDANDO';
            isFrozen = true;
        } else if (diffDays >= 20) {
            timerColor = 'text-red-500 border-red-500/30 animate-pulse';
            timerStatus = `${diffDays} Dias (CRÍTICO)`;
        } else if (diffDays >= 4) {
            timerColor = 'text-orange-500 border-orange-500/30';
            timerStatus = `${diffDays} Dias (Atenção)`;
        }
    }

    return (
        <>
            <motion.div layout initial={{ opacity: 0 }} animate={{ opacity: 1 }} className={`card-elite group border-2 p-6 relative overflow-hidden bg-[var(--bg-card)] transition-all ${isRottingAlert ? 'border-red-600 shadow-[0_0_50px_rgba(220,38,38,0.3)]' : 'border-[var(--border-color)]'}`}>
                
                {isRottingAlert && (
                    <div className="absolute top-0 left-0 right-0 bg-red-600 text-white text-[9px] font-black uppercase text-center py-1 tracking-[0.3em] animate-pulse">
                        ⚠️ ALERTA SUPREMO: LEAD APODRECENDO NA FILA ⚠️
                    </div>
                )}

                <div className={`flex justify-between items-start mb-6 ${isRottingAlert ? 'mt-4' : ''}`}>
                    <div className="space-y-4 w-full">
                        <div className="flex items-center gap-3">
                            <span className="text-[9px] font-black uppercase opacity-40 flex items-center gap-2 tracking-[0.15em]"><FiTarget className="text-accent" /> ORIGEM: {origem}</span>
                        </div>
                        
                        <div className="space-y-1">
                            <h3 className="text-2xl font-black uppercase text-[var(--text-primary)] leading-tight flex items-center gap-2">
                                <FiUser className="text-[var(--accent-color)]" /> {nome}
                            </h3>
                            
                            <div className="flex items-center gap-3 pt-1">
                                <div className="flex items-center gap-2 p-1.5 px-3 rounded-lg bg-white/5 border border-white/10">
                                    <FiBriefcase size={10} className="text-accent opacity-50" />
                                    <span className="text-[10px] font-black text-[var(--text-secondary)] tracking-widest truncate max-w-[200px]">{empresa}</span>
                                </div>
                            </div>
                        </div>

                        {/* ⏳ TEMPORIZADOR COMPACTO (Substitui o Token) */}
                        <div className={`flex items-center gap-4 p-4 px-6 rounded-2xl bg-black/30 border shadow-xl ${timerColor}`}>
                            <FiClock size={16} className="text-current" />
                            <div className="flex flex-col">
                                <span className="text-[7px] font-black uppercase opacity-60 leading-none mb-1.5 tracking-[0.2em]">Idade do Lead</span>
                                <span className="text-sm font-black tracking-widest">{timerStatus}</span>
                            </div>
                        </div>
                    </div>
                    
                    <div className="flex flex-col gap-2">
                        <div className={`px-4 py-1.5 rounded-xl text-[9px] font-black uppercase text-white shadow-xl border border-white/5 ${currentStatusData.color}`}>
                            {currentStatusData.label}
                        </div>
                        {lead.statusComercial === 'NEGOCIACAO' && (
                            <button 
                                onClick={() => setShowOrderModal(true)}
                                onPointerDown={(e) => e.stopPropagation()}
                                className="flex items-center justify-center gap-2 px-3 py-1.5 bg-yellow-500 hover:bg-yellow-400 text-black font-black text-[9px] uppercase tracking-widest rounded-lg border border-yellow-400/50 transition-all shadow-[0_0_15px_rgba(234,179,8,0.3)]"
                            >
                                <FiActivity size={10} /> PEDIDO
                            </button>
                        )}
                    </div>
                </div>

                <div className="space-y-4 mb-6 py-6 border-y border-[var(--border-color)]">
                    <DataRow icon={FiFileText} label="CNPJ" value={cnpj} />
                    <DataRow icon={FiPhone} label="Telefone" value={lead.phone || lead.telefone} bold />
                    <DataRow icon={FiMail} label="Email" value={lead.email} />
                </div>

                <div className="grid grid-cols-3 gap-3 mb-6">
                    <ActionButton icon={FiClock} label="Histórico" onClick={() => setModalOpen('history')} />
                    <ActionButton icon={FiList} label="Logs" onClick={() => setModalOpen('log')} />
                    <ActionButton icon={FiEye} label="Ver Dossiê" onClick={() => setModalOpen('details')} />
                </div>

                {!readOnly && !isFrozen && (
                    <div className="space-y-3">
                        <label className="text-[9px] font-black uppercase opacity-30 px-1 flex items-center gap-2"><FiActivity size={12} className="text-accent" /> Atualizar Funil</label>
                        <div className="relative">
                            <select 
                                value={lead.statusComercial} 
                                onChange={(e) => { setPendingStatus(e.target.value); setModalOpen('status_comment'); }} 
                                onPointerDown={(e) => e.stopPropagation()}
                                className="w-full h-14 px-6 rounded-xl cursor-pointer font-black text-[11px] uppercase text-[var(--text-primary)] bg-[var(--bg-main)] border border-[var(--border-color)] appearance-none focus:border-accent transition-all"
                            >
                                {LEAD_STATUS_MAP.map(status => <option key={status.id} value={status.id} className="text-white bg-black">{status.label}</option>)}
                            </select>
                            <FiChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-accent pointer-events-none text-lg" />
                        </div>
                    </div>
                )}
            </motion.div>

            {/* MODALS PERSISTENTES */}
            <AnimatePresence>
                {modalOpen === 'history' && <LeadHistoryModal lead={lead} onClose={() => setModalOpen(null)} />}
                {modalOpen === 'log' && <LeadLogModal lead={lead} onClose={() => setModalOpen(null)} />}
                
                {/* 🛡️ RENDERIZAÇÃO DO DOSSIÊ SUPREMO */}
                {modalOpen === 'details' && <SupremeLeadModal lead={lead} onClose={() => setModalOpen(null)} />}
                
                {modalOpen === 'status_comment' && (
                    <StatusUpdateLeadModal 
                        onClose={() => setModalOpen(null)} 
                        onConfirm={(comentario, operador) => onStatusUpdate(lead._id, pendingStatus, operador, comentario)} 
                        newStatus={LEAD_STATUS_MAP.find(s => s.id === pendingStatus)?.label || pendingStatus} 
                        currentStatus={currentStatusData.label} 
                    />
                )}
            </AnimatePresence>

            {showOrderModal && (
                <ManualOrderModal 
                    leadSource={lead} 
                    onClose={() => setShowOrderModal(false)}
                    onSuccess={() => {
                        setShowOrderModal(false);
                        onStatusUpdate && onStatusUpdate(lead._id, 'CONCLUIDO');
                    }}
                />
            )}
        </>
    );
}

// HELPERS DE INTERFACE COMPACTOS
const ActionButton = ({ icon: Icon, label, onClick }) => (
    <button onClick={onClick} onPointerDown={(e) => e.stopPropagation()} className="flex flex-col items-center justify-center p-4 rounded-xl border border-[var(--border-color)] bg-[var(--bg-main)] hover:bg-[var(--accent-color)] hover:text-white transition-all group/btn shadow-md active:scale-90">
        <Icon size={18} className="mb-1.5 text-accent group-hover/btn:text-white" />
        <span className="text-[7px] font-black uppercase opacity-40 group-hover/btn:opacity-100">{label}</span>
    </button>
);

const DataRow = ({ icon: Icon, label, value, bold }) => (
    <div className="flex items-start gap-4 group/row">
        <div className="p-2 rounded-lg bg-[var(--bg-main)] border border-[var(--border-color)] text-accent opacity-60 group-hover/row:opacity-100 transition-all shadow-inner"><Icon size={14} /></div>
        <div className="flex flex-col min-w-0">
            <span className="text-[7px] font-black uppercase opacity-30 tracking-widest mb-0.5">{label}</span>
            <span className={`text-[11px] uppercase truncate ${bold ? 'font-black text-[var(--text-primary)]' : 'font-bold text-[var(--text-secondary)]'}`}>{value || '---'}</span>
        </div>
    </div>
);
